import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { API_BASE_URL } from '../../utils/constants';
import { getAuthToken } from '../../utils/auth';

const WorkflowVersionHistory = ({ open, onClose, workflowId }) => {
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [leftVersion, setLeftVersion] = React.useState(null);
  const [rightVersion, setRightVersion] = React.useState(null);
  const [diff, setDiff] = React.useState(null);
  const [jsonView, setJsonView] = React.useState(null);
  const [notes, setNotes] = React.useState('');

  const fetchVersions = React.useCallback(async () => {
    if (!open || !workflowId) return;
    try {
      setLoading(true);
      setError('');
      const token = getAuthToken();
      if (!token) {
        setError('Missing auth token');
        setLoading(false);
        return;
      }
      const resp = await fetch(`${API_BASE_URL}/api/workflow/${workflowId}/versions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setVersions(data || []);
    } catch (e) {
      setError(`Failed to load versions: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [open, workflowId]);

  React.useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const viewJson = async (versionNumber) => {
    try {
      setJsonView(null);
      const token = getAuthToken();
      if (!token) return;
      const resp = await fetch(`${API_BASE_URL}/api/workflow/${workflowId}/versions/${versionNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setJsonView(data?.jsonDefinition ? JSON.parse(data.jsonDefinition) : data?.jsonDefinition || data);
    } catch (e) {
      setError(`Failed to load version JSON: ${e.message}`);
    }
  };

  const compare = async () => {
    setDiff(null);
    if (!leftVersion || !rightVersion) return;
    try {
      const token = getAuthToken();
      if (!token) return;
      // Get right version JSON
      const rightResp = await fetch(`${API_BASE_URL}/api/workflow/${workflowId}/versions/${rightVersion}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!rightResp.ok) throw new Error(`HTTP ${rightResp.status}`);
      const rightData = await rightResp.json();
      const proposedDefinition = rightData?.jsonDefinition ? JSON.parse(rightData.jsonDefinition) : rightData?.jsonDefinition || rightData?.definition;
      // Ask backend to diff base=left vs proposed=right
      const diffResp = await fetch(`${API_BASE_URL}/api/workflow/${workflowId}/diff`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ proposedDefinition, baseVersion: leftVersion })
      });
      if (!diffResp.ok) throw new Error(`HTTP ${diffResp.status}`);
      const diffJson = await diffResp.json();
      setDiff(diffJson);
    } catch (e) {
      setError(`Failed to compute diff: ${e.message}`);
    }
  };

  const activate = async (versionNumber) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const resp = await fetch(`${API_BASE_URL}/api/workflow/${workflowId}/versions/${versionNumber}/activate`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: notes && notes.trim() ? notes.trim() : `Activate v${versionNumber}` })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await fetchVersions();
      setNotes('');
      alert(`Activated version ${versionNumber}`);
    } catch (e) {
      setError(`Failed to activate version: ${e.message}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Workflow Version History</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
        )}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Activation Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Left (base) version"
                  size="small"
                  value={leftVersion || ''}
                  onChange={(e) => setLeftVersion(parseInt(e.target.value || ''))}
                />
                <CompareArrowsIcon color="action" />
                <TextField
                  label="Right (proposed) version"
                  size="small"
                  value={rightVersion || ''}
                  onChange={(e) => setRightVersion(parseInt(e.target.value || ''))}
                />
                <Button variant="outlined" onClick={compare} disabled={!leftVersion || !rightVersion}>Compare</Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Current</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6}>Loading...</TableCell></TableRow>
              )}
              {!loading && versions?.length === 0 && (
                <TableRow><TableCell colSpan={6}>No versions found.</TableCell></TableRow>
              )}
              {!loading && versions?.map((v) => (
                <TableRow key={v.id || `${v.versionNumber}`}>
                  <TableCell>v{v.versionNumber}</TableCell>
                  <TableCell>{v.isCurrent ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{v.createdAt}</TableCell>
                  <TableCell>{v.createdBy}</TableCell>
                  <TableCell>{v.notes}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View JSON">
                      <IconButton size="small" onClick={() => viewJson(v.versionNumber)}>
                        <VisibilityIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Activate this version">
                      <span>
                        <IconButton size="small" onClick={() => activate(v.versionNumber)} disabled={v.isCurrent}>
                          <RocketLaunchIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {diff && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Diff Result</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Adds: {diff?.stats?.adds || 0}, Removes: {diff?.stats?.removes || 0}, Replaces: {diff?.stats?.replaces || 0}
            </Typography>
            <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
              <pre>{JSON.stringify(diff?.patch || [], null, 2)}</pre>
            </Paper>
          </Box>
        )}

        {jsonView && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Version JSON</Typography>
            <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
              <pre>{JSON.stringify(jsonView, null, 2)}</pre>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowVersionHistory;
